import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../utils/api';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Lock,
  Person
} from '@mui/icons-material';
import './AdminModern.css';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    // Validate inputs
    if (!credentials.username || !credentials.password) {
      setError('Please enter both username and password');
      return;
    }
    
    // Clear all stored data to prevent header bloat
    localStorage.clear();
    sessionStorage.clear();
    
    setLoading(true);
    setError('');

    // Define loginUrl outside try-catch so it's accessible in error handling
    const loginUrl = buildApiUrl('admin/login');

    try {
      console.log('Attempting login to:', loginUrl);
      console.log('Credentials:', { username: credentials.username, password: '***' });
      
      // Create a fresh axios instance without cookies
      const cleanAxios = axios.create({
        withCredentials: false,
        maxRedirects: 0
      });
      
      const response = await cleanAxios.post(loginUrl, credentials, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 15000, // 15 second timeout
        withCredentials: false
      });
      
      console.log('Login response:', response);
      console.log('Response data:', response.data);
      
      if (response.data && response.data.success) {
        // Store token in localStorage
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminUser', JSON.stringify(response.data.user));
        
        console.log('Login successful, redirecting...');
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard', { replace: true });
      } else {
        console.error('Login failed: response.data.success is not true', response.data);
        setError(response.data?.error || 'Login failed. Invalid response from server.');
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      
      if (err.response) {
        // Server responded with error
        setError(err.response.data?.error || `Login failed: ${err.response.status} ${err.response.statusText}`);
      } else if (err.request) {
        // Request was made but no response received
        console.error('No response received. Backend might be down or CORS blocked the request.');
        console.error('Request URL was:', loginUrl);
        setError(`Network error: Cannot connect to backend server at ${loginUrl}. Please check if the backend is running and CORS is configured correctly.`);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout: The server took too long to respond. Please try again.');
      } else if (err.message === 'Network Error') {
        setError(`Network Error: Cannot reach backend server. This could be due to:
          - Backend server is not running
          - CORS configuration issue
          - SSL certificate problem
          - Firewall blocking the request
          
          Please check: ${loginUrl}`);
      } else {
        // Something else happened
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="admin-page-wrapper">
      <div className="admin-bg" />
      
      <div className="admin-main-container" style={{ 
        maxWidth: '400px', 
        margin: '20px auto',
        width: { xs: '95%', sm: '400px' }
      }}>
        <div className="admin-glass-card" style={{ 
          padding: { xs: '20px', sm: '30px' }
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <AdminPanelSettings sx={{ 
              fontSize: { xs: 40, sm: 50 }, 
              color: '#000', 
              mb: 1.5 
            }} />
            <Typography variant="h4" component="h1" sx={{ 
              color: '#333', 
              fontWeight: '700',
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}>
              🔐 Admin Login
            </Typography>
            <Typography variant="body1" sx={{ 
              color: '#666', 
              fontSize: { xs: '12px', sm: '14px' },
              fontWeight: '500'
            }}>
              Enter your credentials to access the admin dashboard
            </Typography>
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2.5 }}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                required
                autoFocus
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#666', fontSize: { xs: 18, sm: 20 } }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#000',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#000',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    fontWeight: '500',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000',
                    fontWeight: '600',
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={handleChange}
                required
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: '#666', fontSize: { xs: 18, sm: 20 } }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{ color: '#666' }}
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#000',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#000',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    fontWeight: '500',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000',
                    fontWeight: '600',
                  },
                }}
              />
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2.5,
                  backgroundColor: 'rgba(214, 48, 49, 0.1)',
                  border: '1px solid rgba(214, 48, 49, 0.3)',
                  color: '#d63031',
                  '& .MuiAlert-icon': {
                    color: '#d63031'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              className="admin-btn"
              sx={{
                height: { xs: '44px', sm: '48px' },
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: '600',
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#000',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#333',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                  transform: 'none',
                  boxShadow: 'none',
                },
                transition: 'all 0.3s ease',
                mb: 2
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                ' Login to Dashboard'
              )}
            </Button>
          </form>

          
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 